import React from 'react';
import type { FormikProps, FormikHelpers } from 'formik';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import debounce from 'lodash/debounce';

import { DialogActions, DialogContent, Box } from '@mui/material';

import { useTranslation } from 'next-i18next';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Typography from 'src/components/ui/Typography';
import Button from 'src/components/ui/Button';
import TextField from 'src/components/ui/TextField';
import type { ExtendedAdsQueryVariables } from 'src/components/ad/SearchAdsPage';

import { housingTypes } from 'src/constants/searchAdsTypes';

import useAlerts from 'src/hooks/useAlerts';
import type { ModalProps } from 'src/hooks/useModalOpenState';

import type { SavedSearchesResponseWithTotal } from 'src/types/__generated__/graphql';
import { type Search, type GeometryInput, NotificationSubscriptionStatus } from 'src/types/__generated__/graphql';
import { AnalyticsEvent } from 'src/utils/analytics';
import { styledComponent } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { useAdSearchState } from '../contexts/AdSearchStateContext';
import { useSavedSearchMutations } from '../hooks/useSavedSearchMutations';
import { Switch } from 'src/components/ui/Switch';
import { useUserNotificationSubscriptions } from 'src/hooks/useUserNotificationSubscriptions';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialogContent-root {
        padding: 0 1.5rem;
    }
    .MuiDialogActions-root {
        padding: 0 1.5rem 1.5rem 1.5rem;
    }
    .MuiDialogTitle-root {
        padding: 1.5rem 1.5rem 0 1.5rem;
    }
`;

StyledDialog.defaultProps = {
    fullWidth: true,
    maxWidth: 'xs',
};
type FormValues = Omit<Search, 'id' | 'lastViewedAt' | 'isFirstSavedAdSearch'> & {
    id?: string;
};
interface Props extends ModalProps {
    newParameters?: ExtendedAdsQueryVariables; // if !newParameters we just want to rename the search (when we are in the list modal)
    showEmailNotificationsOption?: boolean;
    savedSearch?: ArrayElement<SavedSearchesResponseWithTotal['results']>;
}

export const SaveSearchModal: React.FC<Props> = ({
    open,
    onClose,
    newParameters,
    showEmailNotificationsOption = true,
    savedSearch,
}) => {
    const { t } = useTranslation(['ad', 'notifications', 'common']);
    const userNotificationSubscriptions = useUserNotificationSubscriptions();
    const { addAlert } = useAlerts();
    const { logEventDynamic } = useDynamicLogEvents();

    const { selectedSavedSearch, loadSavedSearch } = useAdSearchState();
    const { updateSavedSearch, saveSavedSearch } = useSavedSearchMutations();

    const yupSchema = Yup.object().shape({
        name: Yup.string().required(t('common:errors.required')),
    });

    const handleNotificationEnabledChanged = debounce(
        (value: boolean, setFieldValue: (field: string, v: boolean) => void) => {
            setFieldValue('emailNotificationsEnabled', value);
        },
        250,
    );

    const handleSubmitForm = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
        try {
            let newPlace: GeometryInput | undefined;
            let parametersUpdated: string | undefined;
            if (newParameters) {
                const { places, placesSource, ...parameters } = newParameters;
                parametersUpdated = JSON.stringify(parameters);
                if (places && Array.isArray(places) && places[0]) {
                    newPlace = {
                        type: places[0].type,
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        coordinates: places[0].coordinates,
                    };
                }
            }

            if (!savedSearch) {
                if (parametersUpdated && newPlace) {
                    const newSavedSearch = await saveSavedSearch({
                        name: values.name,
                        parameters: parametersUpdated,
                        place: newPlace,
                        placeSource: newParameters?.placesSource,
                        emailNotificationsEnabled: values.emailNotificationsEnabled,
                    });
                    if (newSavedSearch) {
                        await loadSavedSearch(newSavedSearch.id, Boolean(newSavedSearch.isFirstSavedAdSearch));
                    }
                }
            } else {
                await updateSavedSearch({
                    id: savedSearch.id,
                    name: values.name,
                    parameters: parametersUpdated,
                    place: newPlace,
                    emailNotificationsEnabled: values.emailNotificationsEnabled,
                    // if !newParameters => case when we are in the list, we just want to rename the saved search, so we just send the name and we don't update the lastViewedAt
                    lastViewedAt: newParameters ? new Date() : undefined,
                });
                if (selectedSavedSearch?.id === savedSearch.id) {
                    await loadSavedSearch(savedSearch.id);
                }
            }

            resetForm();
            onClose();
            logEventDynamic(AnalyticsEvent.SaveAdSearchConfirmation);
            addAlert({
                type: 'success',
                message: savedSearch ? t('ad:searchUpdated') : t('ad:searchSaved'),
            });
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: String(error),
            });
        }
    };

    const generateNameFromParameters = () => {
        let name = newParameters?.place?.cityName ?? newParameters?.place?.description ?? '';
        if (newParameters) {
            housingTypes.forEach((type) => {
                if ((newParameters as Record<string, boolean>)[type]) {
                    name += ` ${t(`ad:propertyTypes.${type}`)}`;
                }
            });
        }

        return name;
    };
    const defaultValues = {
        id: undefined,
        name: generateNameFromParameters(),
        parameters: '',
        emailNotificationsEnabled:
            userNotificationSubscriptions?.savedSearch.emailNotificationsStatus ===
            NotificationSubscriptionStatus.Active,
    };

    return (
        <Formik
            initialValues={
                savedSearch
                    ? {
                          id: savedSearch.id,
                          name: savedSearch.name,
                          parameters: savedSearch.parameters,
                          emailNotificationsEnabled: savedSearch.emailNotificationsEnabled,
                      }
                    : defaultValues
            }
            validationSchema={yupSchema}
            onSubmit={handleSubmitForm}
            enableReinitialize
        >
            {({ isValid, handleSubmit, resetForm, setFieldValue, values }: FormikProps<FormValues>) => (
                <Form>
                    <StyledDialog
                        open={open}
                        onClose={(e) => {
                            resetForm();
                            onClose();
                        }}
                    >
                        <DialogTitle
                            onClose={() => {
                                resetForm();
                                onClose();
                            }}
                        >
                            <Typography variant="h3">
                                {savedSearch ? t('ad:updateMySearch') : t('ad:saveMySearch')}
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <TextField name="name" label={t('ad:searchName')} />
                            {showEmailNotificationsOption && (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    marginBottom="1rem"
                                >
                                    <Typography variant="body2">
                                        {t('notifications:iWantEmailNotificationsforMySearch')}
                                    </Typography>
                                    <Switch
                                        container={false}
                                        value={values.emailNotificationsEnabled}
                                        onChange={(value) => handleNotificationEnabledChanged(value, setFieldValue)}
                                    />
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    resetForm();
                                    onClose();
                                }}
                                variant="outlined"
                            >
                                {t('common:cancel')}
                            </Button>
                            <Button onClick={() => handleSubmit()} disabled={!isValid}>
                                {t('ad:save')}
                            </Button>
                        </DialogActions>
                    </StyledDialog>
                </Form>
            )}
        </Formik>
    );
};
